import React from 'react'
import { SidebarLeft, SidebarRight } from 'iconsax-react'
import styled from 'styled-components'

// Rail collapse/expand toggle — Figma node 1:17503 (vuesax/outline/sidebar-open),
// the collapsed-rail header. The design replaced the earlier chevron-in-a-box with
// the bare sidebar-panel glyph; the glyph is itself a rounded-rect panel, so it
// carries no additional bordered-box chrome (that would read as a double box). The
// wrapper is a transparent 32px hit target with a hover wash + keyboard focus ring.
// Resting tint is --color-text (navy on light, light on dark — matching the sibling
// Logo mask); it warms to --primary on hover.
const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: var(--color-text);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  flex-shrink: 0;

  svg,
  svg path {
    fill: currentColor;
  }

  &:hover:not(:disabled) {
    background-color: var(--sidebar-item-hover-background);
    color: var(--primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--sub-text-color);
    opacity: 0.45;
  }
`

/**
 * CollapseToggle — top-of-rail button that fires onToggle. The sidebar-panel
 * glyph is directional so it signals intent: expanded shows the "collapse to
 * the left" panel (SidebarLeft), collapsed shows the "expand to the right"
 * panel (SidebarRight). aria-label / aria-expanded carry the same intent for AT.
 *
 * @param {object} props
 * @param {boolean} props.collapsed - current collapsed state
 * @param {() => void} props.onToggle - called when the button is clicked
 * @param {boolean} [props.disabled] - inert (Settings panel forces the rail open)
 * @param {string} [props.className]
 * @param {string} [props.dataPendoFeature] - Pendo Feature tag (collapse vs expand
 *   is set by the call site since the same button serves both entry points)
 */
const CollapseToggle = ({ collapsed, onToggle, disabled, className, dataPendoFeature }) => (
  <ToggleButton
    id="side-nav-collapse-toggle"
    type="button"
    data-pendo-feature={dataPendoFeature}
    aria-label={
      disabled
        ? 'Collapse navigation (unavailable in Settings)'
        : collapsed
          ? 'Expand navigation'
          : 'Collapse navigation'
    }
    aria-expanded={!collapsed}
    aria-disabled={disabled || undefined}
    disabled={disabled}
    onClick={disabled ? undefined : onToggle}
    className={className}
  >
    {collapsed ? (
      <SidebarRight variant="Outline" size={24} color="currentColor" />
    ) : (
      <SidebarLeft variant="Outline" size={24} color="currentColor" />
    )}
  </ToggleButton>
)

export default CollapseToggle
